if (!customElements.get("custom-size-form")) {
  customElements.define(
    "custom-size-form",
    class CustomSizeForm extends HTMLElement {
      constructor() {
        super();
        this.mobileMedia = window.matchMedia("(max-width: 768px)");
        this.desktopMount = this.querySelector('[data-custom-size-mount="desktop"]');
        this.mobileMount = this.querySelector('[data-custom-size-mount="mobile"]');
        this.bottomSheet = this.querySelector("bottom-sheet-modal");
        this.sheet = this.querySelector("[data-custom-size-sheet]");
        this.summary = this.querySelector("[data-custom-size-summary]");
        this.summaryGrid = this.querySelector("[data-custom-size-summary-grid]");
        this.customSizeValue = this.normalize(this.dataset.customSizeValue);
        this.wasCustomSize = false;
        this.hasSavedMobileValues = false;
      }

      connectedCallback() {
        this.handleChange = this.handleChange.bind(this);
        this.handleInput = this.handleInput.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handlePointerOver = this.handlePointerOver.bind(this);
        this.handleInvalid = this.handleInvalid.bind(this);
        this.handleFormData = this.handleFormData.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleBottomSheetOpen = this.handleBottomSheetOpen.bind(this);
        this.handleBottomSheetClose = this.handleBottomSheetClose.bind(this);
        this.addEventListener("change", this.handleChange);
        this.addEventListener("input", this.handleInput);
        this.addEventListener("focusin", this.handleFocusIn);
        this.addEventListener("click", this.handleClick);
        this.addEventListener("pointerover", this.handlePointerOver);
        this.addEventListener("invalid", this.handleInvalid, true);
        this.bottomSheet?.addEventListener("bottom-sheet-modal:open", this.handleBottomSheetOpen);
        this.bottomSheet?.addEventListener("bottom-sheet-modal:close", this.handleBottomSheetClose);
        document.addEventListener("keydown", this.handleKeyDown);
        this.mobileMedia.addEventListener("change", this.handleViewportChange);
        this.variantOptions = this.closest("[data-block-id]")?.querySelector("variant-options");
        this.productForm = document.getElementById(this.desktopMount?.dataset.formId);
        this.variantOptions?.addEventListener("change", this.handleChange);
        this.productForm?.addEventListener("formdata", this.handleFormData);
        this.productForm?.addEventListener("submit", this.handleSubmit, true);
        window.addEventListener("variantChangeEnd", this.handleChange);
        this.sync({ initial: true });
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.handleChange);
        this.removeEventListener("input", this.handleInput);
        this.removeEventListener("focusin", this.handleFocusIn);
        this.removeEventListener("click", this.handleClick);
        this.removeEventListener("pointerover", this.handlePointerOver);
        this.removeEventListener("invalid", this.handleInvalid, true);
        this.bottomSheet?.removeEventListener("bottom-sheet-modal:open", this.handleBottomSheetOpen);
        this.bottomSheet?.removeEventListener("bottom-sheet-modal:close", this.handleBottomSheetClose);
        document.removeEventListener("keydown", this.handleKeyDown);
        this.mobileMedia.removeEventListener("change", this.handleViewportChange);
        this.variantOptions?.removeEventListener("change", this.handleChange);
        this.productForm?.removeEventListener("formdata", this.handleFormData);
        this.productForm?.removeEventListener("submit", this.handleSubmit, true);
        window.removeEventListener("variantChangeEnd", this.handleChange);
        this.closeZoom();
        document.getElementById(this.dataset.zoomModalId)?.remove();
        this.unlockPage();
      }

      normalize(value) {
        return String(value || "").trim().toLowerCase();
      }

      decodeHtml(value) {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = String(value || "");
        return textarea.value;
      }

      selectedSizeIsCustom() {
        const checkedOptions = this.variantOptions?.querySelectorAll('input[type="radio"]:checked') || [];
        return Array.from(checkedOptions).some((input) => this.normalize(input.value) === this.customSizeValue);
      }

      handleChange(event) {
        if (event.target?.matches?.("[data-custom-size-policy]")) {
          event.target.classList.toggle("custom-size-fields__input-error", !event.target.checked);
          return;
        }
        if (event.target?.matches?.("[data-unit-toggle]")) {
          this.changeUnit(event.target.closest("[data-custom-size-fields]"), event.target.checked ? "cm" : "in");
          return;
        }
        this.sync();
      }

      handleInput(event) {
        const input = event.target.closest?.("[data-measurement]");
        if (!input) return;
        input.closest(".custom-size-fields__row")?.classList.toggle("is-filled", input.value.trim() !== "");
        this.validateMeasurement(input, { announce: true });
      }

      validateMeasurement(input, { announce = false } = {}) {
        if (!input) return true;
        const invalid = input.value.trim() !== "" && !input.checkValidity();
        input.classList.toggle("custom-size-fields__input-error", invalid);
        const row = input.closest(".custom-size-fields__row");
        row?.classList.toggle("is-invalid", invalid);
        const fieldValidation = row?.querySelector("[data-field-validation]");
        const fields = input.closest("[data-custom-size-fields]");
        const rangeMessage = fields?.dataset.rangeMessage || "Please enter a value within the indicated range.";
        if (fieldValidation) {
          fieldValidation.textContent = invalid ? rangeMessage : "";
          fieldValidation.hidden = !invalid;
        }
        if (announce && invalid) {
          const validation = fields?.querySelector(".custom-size-fields__validation");
          if (validation) {
            validation.textContent = rangeMessage;
            validation.hidden = false;
          }
        } else if (announce && !invalid) {
          const fields = input.closest("[data-custom-size-fields]");
          const validation = fields?.querySelector(".custom-size-fields__validation");
          if (validation && validation.textContent === fields.dataset.rangeMessage) validation.hidden = true;
        }
        return !invalid;
      }

      handleFocusIn(event) {
        const input = event.target.closest?.("[data-measurement]");
        if (!input) return;
        const fields = input.closest("[data-custom-size-fields]");
        const label = fields?.querySelector("[data-active-tip-label]");
        const description = fields?.querySelector("[data-active-tip-description]");
        if (label) label.textContent = `${input.dataset.tipLabel}:`;
        if (description) description.textContent = this.decodeHtml(input.dataset.tipDescription);
        fields?.querySelectorAll("[data-tip-image-key]").forEach((image) => {
          image.hidden = image.dataset.tipImageKey !== input.dataset.measurement;
        });
        this.ensureUnitValue(fields);
      }

      handleClick(event) {
        const openExtraSelect = this.querySelector("[data-extra-length-select].is-open");
        if (openExtraSelect && !event.target.closest("[data-extra-length-select]")) {
          this.closeExtraLengthMenu(openExtraSelect);
        }
        const extraTrigger = event.target.closest?.("[data-extra-length-trigger]");
        if (extraTrigger) {
          const customSelect = extraTrigger.closest("[data-extra-length-select]");
          this.updateExtraLengthTip(customSelect);
          this.toggleExtraLengthMenu(customSelect);
          return;
        }
        const extraOption = event.target.closest?.("[data-extra-length-option]");
        if (extraOption) {
          this.selectExtraLengthOption(extraOption.closest("[data-extra-length-select]"), extraOption.dataset.extraLengthOption);
          return;
        }
        if (event.target.closest("[data-custom-size-close]")) this.closeMobile({ restoreFocus: true });
        if (event.target.closest("[data-custom-size-edit]")) {
          event.stopPropagation();
          this.openMobile();
          return;
        }
        if (event.target.closest("[data-custom-size-save]")) this.saveMobile();
      }

      handlePointerOver(event) {
        const tooltip = event.target.closest?.(".custom-size-fields__tooltip");
        if (!tooltip) return;
        this.querySelectorAll(".custom-size-fields__tooltip:focus-within").forEach((focusedTooltip) => {
          if (focusedTooltip !== tooltip) focusedTooltip.querySelector(".custom-size-fields__tooltip-trigger")?.blur();
        });
      }

      handleKeyDown(event) {
        const extraSelect = this.querySelector("[data-extra-length-select].is-open");
        if (event.key === "Escape" && extraSelect) {
          event.preventDefault();
          this.closeExtraLengthMenu(extraSelect);
          return;
        }
        if (event.key === "Escape" && this.sheet?.classList.contains("is-open")) {
          event.preventDefault();
          this.closeMobile({ restoreFocus: true });
        }

      }

      handleInvalid(event) {
        if (this.selectedSizeIsCustom() && event.target.matches?.("[data-custom-size-policy]")) {
          event.target.classList.add("custom-size-fields__input-error");
        }
        if (!this.selectedSizeIsCustom() || !this.mobileMedia.matches || !this.mobileMount.contains(event.target)) return;
        event.preventDefault();
      }

      handleFormData(event) {
        if (!this.selectedSizeIsCustom()) return;
        const activeMount = this.mobileMedia.matches ? this.mobileMount : this.desktopMount;
        const unitInput = activeMount.querySelector("[data-custom-size-unit-input]");
        this.ensureUnitValue(activeMount.querySelector("[data-custom-size-fields]"));
        event.formData.set("properties[Measurement unit]", unitInput.value === "in" ? "inch" : unitInput.value);
      }

      handleSubmit(event) {
        if (!this.selectedSizeIsCustom()) return;
        const mount = this.mobileMedia.matches ? this.mobileMount : this.desktopMount;
        const fields = mount?.querySelector("[data-custom-size-fields]");
        if (!fields) return;
        const measurements = Array.from(fields.querySelectorAll("[data-measurement]"));
        const policy = fields.querySelector("[data-custom-size-policy]");
        const invalidInput = measurements.find((input) => !input.value.trim() || !input.checkValidity()) ||
          (policy && !policy.checked ? policy : null);
        fields.querySelectorAll("[data-measurement], [data-custom-size-policy]").forEach((input) => {
          input.classList.toggle("custom-size-fields__input-error", input === invalidInput || !input.checkValidity());
        });
        const validation = fields.querySelector(".custom-size-fields__validation");
        if (!invalidInput) {
          if (validation) validation.hidden = true;
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (validation) {
          validation.textContent = fields.dataset.requiredMessage;
          validation.hidden = false;
        }
        if (this.mobileMedia.matches) {
          requestAnimationFrame(() => {
            this.openMobile();
            this.saveMobile();
          });
        } else {
          invalidInput.focus({ preventScroll: true });
        }
      }

      handleViewportChange() {
        this.closeZoom();
        this.closeMobile();
        this.sync({ initial: true });
      }

      sync({ initial = false } = {}) {
        const isCustomSize = this.selectedSizeIsCustom();
        const isMobile = this.mobileMedia.matches;

        this.desktopMount.hidden = !isCustomSize || isMobile;
        this.mobileMount.hidden = !isCustomSize || !isMobile;
        this.summary.hidden = !isCustomSize || !isMobile;
        this.summaryGrid.hidden = !this.hasSavedMobileValues;
        this.setFieldsEnabled(this.desktopMount, isCustomSize && !isMobile);
        this.setFieldsEnabled(this.mobileMount, isCustomSize && isMobile);

        if (!isCustomSize) {
          this.closeZoom();
          this.closeMobile();
        } else if (isMobile && !initial && !this.wasCustomSize) {
          this.openMobile();
        }

        this.wasCustomSize = isCustomSize;
      }

      setFieldsEnabled(mount, enabled) {
        mount?.querySelectorAll("input, select").forEach((input) => {
          input.disabled = !enabled;
          if (input.matches("[data-measurement]")) {
            input.closest(".custom-size-fields__row")?.classList.toggle("is-filled", input.value.trim() !== "");
            if (enabled && input.value.trim() !== "") this.validateMeasurement(input);
          }
        });
        if (enabled) this.ensureUnitValue(mount.querySelector("[data-custom-size-fields]"));
        if (enabled) {
          mount?.querySelectorAll("[data-extra-length-select]").forEach((customSelect) => {
            const select = customSelect.closest(".custom-size-fields__control")?.querySelector("[data-extra-length]");
            const unit = mount.querySelector("[data-custom-size-unit-input]")?.value || "in";
            this.updateExtraLengthOptions(select, unit, select?.value || "");
          });
        }
      }

      ensureUnitValue(fields) {
        if (!fields) return;
        const unitInput = fields.querySelector("[data-custom-size-unit-input]");
        if (unitInput && !unitInput.value) {
          unitInput.value = fields.querySelector(".custom-size-fields__unit-switch")?.dataset.unit || "in";
        }
      }

      changeUnit(fields, unit) {
        if (!fields) return;
        const unitLabel = unit === "in" ? "in" : unit;
        const unitInput = fields.querySelector("[data-custom-size-unit-input]");
        const previousUnit = unitInput?.value || "in";
        if (previousUnit === unit) return;
        const isDesktop = fields.dataset.customSizeFields === "desktop";
        const extraLengthValues = Array.from(fields.querySelectorAll("[data-extra-length]"), (select) => ({
          select,
          value: select.value,
        }));

        fields.querySelectorAll("[data-measurement]").forEach((input) => {
          if (input.value !== "") {
            const convertedValue = unit === "cm"
              ? Math.round(Number(input.value) * 2.54)
              : Math.round(Number(input.value) / 2.54 * 10) / 10;
            input.value = String(convertedValue);
          }
          const convertedMin = Number(unit === "cm" ? input.dataset.minCm : input.dataset.minIn);
          const convertedMax = Number(unit === "cm" ? input.dataset.maxCm : input.dataset.maxIn);
          input.min = String(convertedMin);
          input.max = String(convertedMax);
          input.placeholder = isDesktop
            ? `${convertedMin} - ${convertedMax}`
            : `${input.dataset.tipLabel} (${unitLabel}): ${convertedMin} - ${convertedMax}`;
          input.closest(".custom-size-fields__row")?.classList.toggle("is-filled", input.value.trim() !== "");
        });
        unitInput.value = unit;
        fields.querySelector(".custom-size-fields__unit-switch").dataset.unit = unit;
        fields.querySelectorAll("[data-unit-label]").forEach((label) => {
          label.classList.toggle("is-active", label.dataset.unitLabel === unit);
        });
        fields.querySelectorAll("[data-unit-text]").forEach((label) => {
          label.textContent = unitLabel;
        });
        fields.querySelectorAll("[data-extra-length]").forEach((select) => {
          const previousValue = extraLengthValues.find((item) => item.select === select)?.value || "";
          const convertedValue = previousValue === "" ? "" : this.formatExtraLength(Number(previousValue), unit, previousUnit, select);
          this.updateExtraLengthOptions(select, unit, convertedValue);
        });
        fields.querySelectorAll("[data-range-min-in]").forEach((label) => {
          const convertedMin = Number(unit === "cm" ? label.dataset.rangeMinCm : label.dataset.rangeMinIn);
          const convertedMax = Number(unit === "cm" ? label.dataset.rangeMaxCm : label.dataset.rangeMaxIn);
          const range = label.querySelector(".custom-size-fields__range");
          if (range) range.textContent = `(${unitLabel}): ${convertedMin} - ${convertedMax}`;
        });
      }

      formatExtraLength(value, unit, previousUnit = unit, select) {
        if (unit === previousUnit) return String(value);
        if (unit === "cm") return String(Math.min(Number(select.dataset.maxCm), Math.round(value * 2.54 / 3) * 3));
        return String(Math.min(Number(select.dataset.maxIn), Math.round(value / 2.54)));
      }

      updateExtraLengthOptions(select, unit, value = "") {
        if (!select) return;
        const minimum = Number(unit === "cm" ? select.dataset.minCm : select.dataset.minIn);
        const maximum = Number(unit === "cm" ? select.dataset.maxCm : select.dataset.maxIn);
        const step = unit === "cm" ? 3 : 1;
        const values = [];
        for (let optionValue = minimum; optionValue <= maximum; optionValue += step) values.push(String(optionValue));
        const placeholder = select.querySelector("option[value='']");
        if (placeholder) placeholder.textContent = `<= ${maximum}`;
        Array.from(select.options).slice(1).forEach((option) => option.remove());
        values.forEach((optionValue) => select.add(new Option(optionValue, optionValue)));
        const selectedValue = value === "" ? "" : values.reduce((closest, optionValue) =>
          Math.abs(Number(optionValue) - Number(value)) < Math.abs(Number(closest) - Number(value)) ? optionValue : closest,
        values[0]);
        select.value = selectedValue;
        const customSelect = select.closest(".custom-size-fields__control")?.querySelector("[data-extra-length-select]");
        if (!customSelect) return;
        const menu = customSelect.querySelector("[data-extra-length-menu]");
        // Keep the existing option buttons when the values are unchanged: replacing the clicked button
        // detaches the event target and the surrounding bottom sheet treats the click as an outside click.
        const currentValues = menu ? Array.from(menu.querySelectorAll("[data-extra-length-option]"), (option) => option.dataset.extraLengthOption) : [];
        if (menu && currentValues.join(",") !== values.join(",")) {
          menu.querySelectorAll("[data-extra-length-option]").forEach((option) => option.remove());
          values.forEach((optionValue) => {
            const option = document.createElement("button");
            option.type = "button";
            option.setAttribute("role", "option");
            option.dataset.extraLengthOption = optionValue;
            option.setAttribute("aria-selected", "false");
            option.textContent = `+${optionValue}`;
            menu.append(option);
          });
        }
        this.syncExtraLengthControl(customSelect, selectedValue);
      }

      syncExtraLengthControl(customSelect, value) {
        if (!customSelect) return;
        const select = customSelect.closest(".custom-size-fields__control")?.querySelector("[data-extra-length]");
        const selected = customSelect.querySelector(`[data-extra-length-option="${value}"]`);
        const output = customSelect.querySelector("[data-extra-length-value]");
        const floatingLabel = customSelect.querySelector("[data-extra-length-floating-label]");
        const fields = customSelect.closest("[data-custom-size-fields]");
        const unit = fields?.querySelector("[data-custom-size-unit-input]")?.value || "in";
        const unitLabel = unit === "in" ? "in" : unit;
        const label = select?.dataset.extraLengthLabel || "Extra Length";
        const hasValue = value !== "";
        const limit = Number(unit === "cm" ? select.dataset.maxCm : select.dataset.maxIn);
        const placeholder = fields?.dataset.customSizeFields === "desktop"
          ? `<= ${limit}`
          : `${label} (${unitLabel}): <= ${limit}`;
        customSelect.classList.toggle("is-filled", hasValue);
        customSelect.closest(".custom-size-fields__row")?.classList.toggle("is-filled", hasValue);
        if (floatingLabel) floatingLabel.textContent = placeholder;
        if (output) output.textContent = hasValue ? value : placeholder;
        customSelect.querySelectorAll("[data-extra-length-option]").forEach((option) => {
          option.setAttribute("aria-selected", option === selected ? "true" : "false");
        });
        if (select && select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      toggleExtraLengthMenu(customSelect) {
        if (!customSelect) return;
        const menu = customSelect.querySelector("[data-extra-length-menu]");
        const trigger = customSelect.querySelector("[data-extra-length-trigger]");
        if (!menu || !trigger) return;
        const isOpen = customSelect.classList.toggle("is-open");
        menu.hidden = !isOpen;
        trigger.setAttribute("aria-expanded", String(isOpen));
      }

      closeExtraLengthMenu(customSelect) {
        if (!customSelect) return;
        customSelect.classList.remove("is-open");
        customSelect.querySelector("[data-extra-length-menu]")?.setAttribute("hidden", "");
        customSelect.querySelector("[data-extra-length-trigger]")?.setAttribute("aria-expanded", "false");
      }

      selectExtraLengthOption(customSelect, value) {
        this.syncExtraLengthControl(customSelect, value);
        this.updateExtraLengthTip(customSelect);
        this.closeExtraLengthMenu(customSelect);
      }

      updateExtraLengthTip(customSelect) {
        const fields = customSelect?.closest("[data-custom-size-fields]");
        const select = customSelect?.closest(".custom-size-fields__control")?.querySelector("[data-extra-length]");
        if (!fields || !select) return;
        const label = fields.querySelector("[data-active-tip-label]");
        const description = fields.querySelector("[data-active-tip-description]");
        if (label) label.textContent = `${select.dataset.extraLengthLabel}:`;
        if (description) description.textContent = this.decodeHtml(select.dataset.extraLengthDescription);
      }

      handleBottomSheetOpen() {
        this.sheet.classList.add("is-open");
        this.mobileMount.classList.add("is-open");
        this.sheet.querySelector(".custom-size-form__sheet-body").scrollTop = 0;
        requestAnimationFrame(() => this.sheet.querySelector("input:not([type='hidden'])")?.focus({ preventScroll: true }));
      }

      handleBottomSheetClose() {
        this.sheet.classList.remove("is-open");
        this.mobileMount.classList.remove("is-open");
      }

      openMobile() {
        if (!this.mobileMedia.matches || this.mobileMount.hidden) return;
        this.lastFocus = document.activeElement;
        this.bottomSheet?.open?.();
      }

      closeZoom() {
        const modal = document.getElementById(this.dataset.zoomModalId);
        if (modal?.hasAttribute("open")) modal.hide();
      }

      closeMobile({ restoreFocus = false } = {}) {
        this.bottomSheet?.close?.({ restoreFocus });
        if (restoreFocus) this.lastFocus?.focus();
      }

      unlockPage() {
        document.body.classList.remove("custom-size-modal-open");
      }

      saveMobile() {
        this.ensureUnitValue(this.mobileMount.querySelector("[data-custom-size-fields]"));
        const inputs = Array.from(this.mobileMount.querySelectorAll("[data-measurement], [data-custom-size-policy]"));
        inputs.filter((input) => input.matches("[data-measurement]") && !input.value.trim()).forEach((input) => {
          const row = input.closest(".custom-size-fields__row");
          input.classList.add("custom-size-fields__input-error");
          row?.classList.add("is-invalid");
          const fieldValidation = row?.querySelector("[data-field-validation]");
          if (fieldValidation) {
            fieldValidation.textContent = "Please enter a measurement.";
            fieldValidation.hidden = false;
          }
        });
        const invalidInput = inputs.find((input) => input.matches("[data-measurement]") ? !input.value.trim() || !this.validateMeasurement(input) : !input.checkValidity());
        const policy = this.mobileMount.querySelector("[data-custom-size-policy]");
        policy?.classList.toggle("custom-size-fields__input-error", !policy.checked);
        if (invalidInput) {
          const validation = this.mobileMount.querySelector(".custom-size-fields__validation");
          if (validation) {
            validation.textContent = invalidInput.matches("[data-measurement]")
              ? invalidInput.closest(".custom-size-fields__row")?.querySelector("[data-field-validation]")?.textContent || "Please enter a measurement."
              : this.mobileMount.querySelector("[data-custom-size-fields]")?.dataset.requiredMessage;
            validation.hidden = false;
          }
          invalidInput.focus();
          return;
        }

        this.mobileMount.querySelectorAll("[data-measurement]").forEach((input) => {
          const output = this.summary.querySelector(`[data-summary-value="${input.dataset.measurement}"]`);
          if (output) output.textContent = input.value;
        });
        const extraLength = this.mobileMount.querySelector("[data-extra-length]");
        const extraLengthOutput = this.summary.querySelector('[data-summary-value="extra-length"]');
        if (extraLengthOutput) {
          const extraValue = extraLength?.value || "";
          extraLengthOutput.textContent = extraValue || "0";
        }
        this.hasSavedMobileValues = true;
        this.summaryGrid.hidden = false;
        this.closeMobile();
      }
    },
  );
}
